import { useNavigate } from '@tanstack/react-router';
import { businessPath, modulePath, path } from '~/app/navigation';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES, type Locale } from '@prody/shared';
import {
  Button, Code, IconSearch, Menu, Segmented, Stamp, Tooltip,
} from '~/design-system';
import { useAppearance, type Density, type Theme } from '~/platform/appearance/appearance';
import { changeLocale } from '~/platform/i18n/i18n';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import './TopBar.css';

/* ------------------------------------------------------------------ *
 * The top bar carries three things and nothing else: which business
 * you are in, which trading day it is, and how to find anything.
 *
 * The trading day is up here rather than inside a module because in
 * this business it is the primary axis of everything — the buyer, the
 * picker, the driver and the invoicing clerk are all working a *day*,
 * and half the bugs in food distribution software come from two
 * screens disagreeing about which one.
 * ------------------------------------------------------------------ */

export function TopBar({ onOpenSearch }: { onOpenSearch: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { business, user, role, format, otherBusinesses } = useBusiness();
  const { theme, density, setTheme, setDensity } = useAppearance();

  const tradingDay = format.tradingDay();
  const isCalendarToday = tradingDay === new Date().toISOString().slice(0, 10);

  return (
    <header className="pd-topbar">
      {/* --- Business switcher --- */}
      <Menu
        label={t('business.switch')}
        align="start"
        trigger={
          <button type="button" className="pd-topbar__biz">
            <span className="pd-topbar__biz-name truncate">{business.name}</span>
            <Stamp tone="neutral" size="sm">
              {t(`role.${role}`)}
            </Stamp>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" aria-hidden="true">
              <path d="M4 6.5 8 10.5l4-4" />
            </svg>
          </button>
        }
        items={[
          ...otherBusinesses.map((b) => ({
            key: b.id,
            label: b.name,
            onSelect: () => void navigate({ to: path(businessPath(b.slug)) }),
          })),
          {
            key: 'add',
            label: t('business.addNew'),
            separatorBefore: otherBusinesses.length > 0,
            onSelect: () => void navigate({ to: path('/onboarding') }),
          },
        ]}
      />

      {/* --- Trading day ---
          Shown as a stamp when it differs from the calendar date,
          which happens every night between midnight and 03:00. */}
      <Tooltip
        content={
          isCalendarToday
            ? format.date(tradingDay, 'weekday')
            : `${format.date(tradingDay, 'weekday')} · ${t('settings.dayStartsAtHint')}`
        }
      >
        <div className="pd-topbar__day" data-shifted={!isCalendarToday || undefined}>
          <span className="stencil">{t('market:tradingDay')}</span>
          <Code>{format.date(tradingDay, 'weekday')}</Code>
        </div>
      </Tooltip>

      {/* --- Search ---
          A button, not an input. The real interface is the palette;
          putting a dead text field in the chrome that has to be
          clicked to become useful is theatre. */}
      <button type="button" className="pd-topbar__search" onClick={onOpenSearch}>
        <IconSearch />
        <span className="truncate">{t('search.placeholder')}</span>
        <kbd className="num">Ctrl K</kbd>
      </button>

      <div className="grow" />

      {/* --- Appearance ---
          Two segmented controls rather than a settings menu. Theme and
          density change several times a day here — a buyer switches to
          Hall + market density when they get out of the van — and
          burying a twice-daily control two clicks deep is a tax. */}
      <div className="pd-topbar__appearance">
        <Segmented<Theme>
          label={t('appearance.theme')}
          size="sm"
          value={theme}
          onChange={setTheme}
          options={[
            { value: 'hall', label: t('appearance.themeHall'), title: t('appearance.themeHallHint') },
            { value: 'office', label: t('appearance.themeOffice'), title: t('appearance.themeOfficeHint') },
            { value: 'auto', label: 'Auto', title: t('appearance.themeAuto') },
          ]}
        />
        <Segmented<Density>
          label={t('appearance.density')}
          size="sm"
          value={density}
          onChange={setDensity}
          options={[
            { value: 'compact', label: 'S', title: t('appearance.densityCompact') },
            { value: 'comfortable', label: 'M', title: t('appearance.densityComfortable') },
            { value: 'market', label: 'L', title: t('appearance.densityMarketHint') },
          ]}
        />
      </div>

      {/* --- Language --- */}
      <Menu
        label={t('appearance.language')}
        trigger={
          <Button variant="quiet" size="sm" aria-label={t('appearance.language')}>
            {(i18n.resolvedLanguage ?? 'en').slice(0, 2).toUpperCase()}
          </Button>
        }
        items={SUPPORTED_LOCALES.filter((l) => business.settings.enabledLocales.includes(l)).map(
          (locale) => ({
            key: locale,
            label: LOCALE_LABEL[locale],
            onSelect: () => void changeLocale(locale),
          }),
        )}
      />

      {/* --- User --- */}
      <Menu
        label={user.name}
        trigger={
          <button type="button" className="pd-topbar__user" aria-label={user.name}>
            {user.name
              .split(' ')
              .slice(0, 2)
              .map((w) => w[0])
              .join('')}
          </button>
        }
        items={[
          { key: 'profile', label: user.email, disabled: true },
          {
            key: 'settings',
            label: t('settings:title'),
            separatorBefore: true,
            onSelect: () => void navigate({ to: path(modulePath(business.slug, 'settings')) }),
          },
          { key: 'signout', label: 'Sign out', tone: 'danger', onSelect: () => undefined },
        ]}
      />
    </header>
  );
}

const LOCALE_LABEL: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  ru: 'Русский',
};
